/**
 * UTERIFIC header.
 *
 * - Publishes the header height as `--ut-header-height` (and Horizon's `--header-height`)
 *   so templates without a full-bleed banner can offset their content below the fixed bar.
 * - Drives the full-screen menu <dialog>: open/close, click-outside, and focus return.
 * - Adds `is-scrolled` once the page is scrolled, and `is-scrolling` only while it is
 *   actually moving. The overlay bar's glass needs both, so it appears under a moving page
 *   and is gone again a moment after the page settles.
 * - Adds `is-on-dark` while a section marked data-ut-header="light" is behind the bar, which
 *   turns the wordmark white.
 */
import '@theme/component';

/* WCAG 2.x relative luminance and contrast ratio, for deciding whether the red bar
   elements can stay red over whatever ground is behind them. */
const RED = [183, 13, 46];
const CREAM = [245, 232, 210];

const channel = (v) => {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};
const luminance = ([r, g, b]) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
const contrast = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};
const parseColor = (value) => {
  const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+%?))?\s*\)/.exec(value || '');
  if (!m) return null;
  let alpha = m[4] === undefined ? 1 : parseFloat(m[4]);
  if (m[4] && m[4].endsWith('%')) alpha /= 100;
  return [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]), alpha];
};

class UterificHeader extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.logo = this.querySelector('.ut-header__logo');
    this.actions = this.querySelector('.ut-header__actions');
    this.openButton = this.querySelector('[data-ut-menu-open]');
    this.closeButtons = this.querySelectorAll('[data-ut-menu-close]');

    this.openButton?.addEventListener('click', this.open);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
    this.dialog?.addEventListener('click', this.onBackdropClick);
    this.dialog?.addEventListener('close', this.onClose);
    // Section links on the same page never navigate, so the dialog would stay open over
    // the section it just scrolled to. Close on any link, then let the browser follow it.
    this.dialog?.addEventListener('click', this.onLinkClick);

    this.measure();
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this);

    // Above 990px Horizon scrolls inside .page-wrapper, not the window, and scroll events
    // do not bubble — a window listener never fires there, which is why the bar stayed
    // transparent on desktop while going solid on phones. Capturing on the document sees
    // scroll from either container.
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    this.onScroll();

    // Horizon swaps which element scrolls at 990px: the window below it, .page-wrapper
    // above. The position lives in whichever one was scrolling, so crossing the line
    // while resizing landed the visitor back at the top. Hand the last offset across.
    this.phone = window.matchMedia('(max-width: 749px)');
    this.phone.addEventListener('change', this.onScroll);
    this.desktopScroller = window.matchMedia('(min-width: 990px)');
    this.desktopScroller.addEventListener('change', this.onScrollerSwap);
  }

  disconnectedCallback() {
    clearTimeout(this.scrollIdle);
    this.resizeObserver?.disconnect();
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    this.desktopScroller?.removeEventListener('change', this.onScrollerSwap);
    this.phone?.removeEventListener('change', this.onScroll);
  }

  measure() {
    const height = this.getBoundingClientRect().height;
    if (!height) return;
    document.documentElement.style.setProperty('--ut-header-height', `${Math.round(height)}px`);
    document.body.style.setProperty('--header-height', `${Math.round(height)}px`);
  }

  onScrollerSwap = (event) => {
    const offset = this.lastOffset || 0;
    if (!offset) return;
    requestAnimationFrame(() => {
      const wrapper = document.querySelector('.page-wrapper');
      if (event.matches && wrapper) {
        wrapper.scrollTo({ top: offset, behavior: 'instant' });
      } else {
        window.scrollTo({ top: offset, behavior: 'instant' });
      }
    });
  };

  onScroll = () => {
    const wrapper = document.querySelector('.page-wrapper');
    const offset = Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
    this.lastOffset = offset;
    this.classList.toggle('is-scrolled', offset > 24);

    // The glass belongs to the movement, not to the position. It exists to keep copy from
    // colliding with the wordmark as the page slides past, and once the page is still
    // there is nothing to separate — so the bar goes back to being nothing at all.
    //
    // 180ms of quiet before it goes. Long enough to ride through the gaps between events
    // during a momentum fling, short enough that it has already begun fading by the time
    // a reader has settled on what stopped them.
    this.classList.add('is-scrolling');
    clearTimeout(this.scrollIdle);
    this.scrollIdle = setTimeout(() => this.classList.remove('is-scrolling'), 180);

    // The wordmark is the brand's red and stays red. The one exception is a section
    // marked data-ut-header="light": the lacquer band and flood, where red on red would
    // vanish. Those marks turn the whole bar cream while they sit behind it.
    const bar = this.getBoundingClientRect().height;
    // It has to cover the bar, not merely still be on screen. `bottom > 0` was true for a
    // mark whose last pixel was level with the wordmark, so the bar stayed cream through
    // the hand-off from the exploded act into the offer band below it — white type on a
    // sand panel, which is the one thing the mark exists to prevent. Requiring the mark to
    // reach the bar's bottom edge ends it the moment the ground under the bar is no longer
    // the marked one. While the act is up the pinned frame is a screen tall at top 0, so it
    // clears this as easily as it cleared the old test.
    const marked = Array.from(document.querySelectorAll('[data-ut-header="light"]')).some(
      (section) => {
        const rect = section.getBoundingClientRect();
        return rect.top < bar && rect.bottom >= bar;
      }
    );
    // On phones the wordmark follows the ground the way the controls do: cream over the
    // hero photograph and the dark panels, red elsewhere.
    this.logo?.classList.toggle(
      'is-on-dark',
      this.wantsLight(this.logo, marked || (this.phone.matches && this.underMark(this.logo, 'light-actions')))
    );

    // The controls on the right go cream more readily: over a photograph marked
    // data-ut-header="light-actions" (the hero, while it still fills the bar's corner),
    // and over any measured ground where cream reads better than red.
    this.actions?.classList.toggle(
      'is-on-dark',
      this.wantsLight(this.actions, marked || this.underMark(this.actions, 'light-actions'))
    );
  };

  /** Whether an element marked data-ut-header=<mark> sits under the part's centre. */
  underMark(part, mark) {
    const rect = part.getBoundingClientRect();
    if (!rect.width) return false;
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    return Array.from(document.querySelectorAll(`[data-ut-header="${mark}"]`)).some((el) => {
      const r = el.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    });
  }

  /**
   * Whether this part should go cream rather than red, on the ground actually behind it.
   *
   * The ground decides whenever it can be read: cream on a dark panel, red on a light one,
   * whichever of the two carries further. That is one comparison rather than a threshold,
   * which matters on the pale grounds — blush measures 3.85:1 against red, under WCAG's
   * 4.5, but cream on blush is 1.45:1. Failing the threshold is not a reason to reach for
   * something three times worse.
   *
   * A declared ground outranks a measured one, because the layers that declare themselves
   * are the ones the measurement cannot reach. They are decorative, so they carry
   * pointer-events: none, and elementsFromPoint skips those entirely — the colour that
   * comes back is whatever sits *behind* the overlay, which is not the ground at all. The
   * exploded band's lacquer flood is exactly this: a full-bleed red sheet over a sand
   * section, measured as sand, and the bar stayed red on a red screen.
   *
   * So the mark wins where it is set, and everything else is measured: cream on a dark
   * ground, red on a light one, whichever of the two carries further. That is one
   * comparison rather than a threshold, which matters on the pale grounds — blush measures
   * 3.85:1 against red, under WCAG's 4.5, but cream on blush is 1.45:1. Failing the
   * threshold is not a reason to reach for something three times worse.
   *
   * This puts the accuracy of the marks on the sections that set them, which is where it
   * belongs: a mark raised before its ground has actually arrived is a mark that lies.
   */
  wantsLight(part, hinted) {
    if (!part) return false;
    if (hinted) return true;
    const rect = part.getBoundingClientRect();
    if (!rect.width) return false;
    const ground = this.groundAt(rect.left + rect.width / 2, rect.top + rect.height / 2);
    if (!ground) return false;
    return contrast(CREAM, ground) > contrast(RED, ground);
  }

  /** The first opaque background colour under a point, ignoring the header itself. */
  groundAt(x, y) {
    if (!document.elementsFromPoint) return null;
    const stack = document.elementsFromPoint(x, y).filter((el) => !this.contains(el));
    for (const el of stack) {
      const tag = el.tagName;
      if (tag === 'IMG' || tag === 'VIDEO' || tag === 'CANVAS' || tag === 'PICTURE') return null;
      const style = getComputedStyle(el);
      if (style.backgroundImage !== 'none') return null;
      const color = parseColor(style.backgroundColor);
      if (color && color[3] >= 0.5) return color;
    }
    return null;
  }

  open = () => {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    this.openButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ut-menu-open');
  };

  close = () => {
    this.dialog?.close();
  };

  onClose = () => {
    this.openButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('ut-menu-open');
    this.openButton?.focus({ preventScroll: true });
  };

  onBackdropClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  onLinkClick = (event) => {
    if (event.target.closest('a[href]')) this.close();
  };
}

if (!customElements.get('uterific-header')) {
  customElements.define('uterific-header', UterificHeader);
}
